import React, { useState, useRef, useEffect } from 'react'
import DOMPurify from 'dompurify'
import Dropdown from './Dropdown'
import { NavMenuItemsProps } from './Navigation.types'
import { ANALYTICS_TYPES } from '../../../SiteNav/util'
import { handleLinkClick } from '../utils/common-utils'
import { MenuParameters } from '../data/data.types'

// Main Nav Items. (depth 0)
const NavMenuItem = ({
  item,
  depthLevel,
  index,
  currentFocusMenu,
  totalSubmenus,
  firstNavItemRef,
  styles,
  activeTab,
  updateCurrFocusedIndex,
  updateActiveTab,
  textSizeClass,
  menuIconDisable,
  isRightButton,
  enableAnalytics,
  parentRef,
}: NavMenuItemsProps) => {
  const [dropDownVisible, setDropDownVisible] = useState<boolean>(false)
  const [focusFirstItem, setFocusFirstItem] = useState<boolean>(false)
  const [mouseOver, setMouseOver] = useState<boolean>(false)
  const showDropdown: boolean = item?.HstMenuItemRepresentation?.parameters?.showDropdown === 'true'
  const hasSubmenu: boolean = Boolean(item?.HstMenuItemRepresentation?.menuItems?.length)
  const linkRef = useRef<HTMLAnchorElement>(null)
  const dataAnalyticsValue =
    item?.HstMenuItemRepresentation?.parameters?.['data-analytics'] ??
    item.HstMenuItemRepresentation.name
  let usedNavRef = firstNavItemRef || linkRef
  const dropdownHideTimeout = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (index === currentFocusMenu && usedNavRef?.current) {
      usedNavRef.current.focus()
    }
  }, [currentFocusMenu, usedNavRef.current])
  useEffect(() => {
    if (currentFocusMenu !== index && dropDownVisible) {
      setDropDownVisible(false)
    }
  }, [currentFocusMenu])
  const onMouseEnterLink = () => {
    if (dropdownHideTimeout.current) {
      clearTimeout(dropdownHideTimeout.current)
      dropdownHideTimeout.current = null
    }
    setDropDownVisible(showDropdown && hasSubmenu)
    setMouseOver(true)
  }
  useEffect(() => {
    return () => {
      if (dropdownHideTimeout.current) {
        clearTimeout(dropdownHideTimeout.current)
      }
    }
  }, [])
  const onMouseLeaveLink = (e: React.MouseEvent) => {
    if (
      e.relatedTarget &&
      e.relatedTarget instanceof Node &&
      parentRef?.current?.contains(e.relatedTarget)
    ) {
      setDropDownVisible(false)
      setMouseOver(false)
      setFocusFirstItem(false)
      return
    }
    setMouseOver(false)
    setFocusFirstItem(false)
    dropdownHideTimeout.current = setTimeout(() => {
      setDropDownVisible(false)
    }, 250)
  }
  const onFocus = () => {
    if (dropDownVisible) {
      setDropDownVisible(false)
    }
    updateCurrFocusedIndex(index)
  }
  const onKeyDown = (e: React.KeyboardEvent<HTMLAnchorElement>) => {
    const enterSpace: string[] = ['Enter', ' ']
    const dropdownMenu = showDropdown && hasSubmenu
    if (enterSpace.includes(e.key) && !dropDownVisible && dropdownMenu) {
      e.preventDefault()
      e.stopPropagation()
      setDropDownVisible(dropdownMenu)
      setFocusFirstItem(false)
    } else if (
      e.key === ' ' &&
      document.activeElement instanceof HTMLAnchorElement &&
      dropDownVisible &&
      dropdownMenu
    ) {
      document.activeElement?.click()
    } else if (e.key === 'ArrowDown' && dropdownMenu) {
      e.preventDefault()
      e.stopPropagation()
      setDropDownVisible(dropdownMenu)
      setFocusFirstItem(dropdownMenu)
    } else if (
      dropDownVisible &&
      (e.key === 'Tab' || (e.key === 'Tab' && e.shiftKey) || e.key === 'Escape')
    ) {
      setDropDownVisible(false)
      setFocusFirstItem(false)
      updateCurrFocusedIndex(index)
    }
  }

  const linkStyle: {
    color: string
    borderBottomColor: string | undefined
  } = {
    color:
      mouseOver || activeTab === item.HstMenuItemRepresentation.name
        ? styles.linkTextColorHover
        : styles.linkTextColor,
    borderBottomColor: undefined,
  }
  if (activeTab === item.HstMenuItemRepresentation.name) {
    linkStyle.borderBottomColor = styles.navTabSelectedBorderBottom
  }
  if (mouseOver || dropDownVisible) {
    linkStyle.borderBottomColor = styles.navTabHoverBorderBottom
  }
  const type = isRightButton ? ANALYTICS_TYPES.NAV_TRAY : ANALYTICS_TYPES.NAV
  const disabled = item.HstMenuItemRepresentation.disabled
  return (
    <li
      onMouseEnter={onMouseEnterLink}
      onMouseLeave={onMouseLeaveLink}
      className={`menu-items menu-items-${depthLevel} ${textSizeClass} `}
      data-testid={`li-${item.HstMenuItemRepresentation.name.replace(/ /g, '')} ${
        disabled && 'u-disabled'
      }`}
      role={disabled ? undefined : 'menuitem'}
    >
      <a
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        onClick={e => {
          if (disabled) {
            e.preventDefault()
            e.stopPropagation()
          } else {
            const href = item.HstMenuItemRepresentation.url
            if (href.includes('#')) {
              const hash = '#' + href.split('#')[1]
              const target = document.querySelector(hash)
              if (target) {
                e.preventDefault()
                target.scrollIntoView({ behavior: 'smooth' })
                history.pushState(null, '', hash)
                if (updateActiveTab) {
                  updateActiveTab(item.HstMenuItemRepresentation.name)
                }
              }
            }
            if (enableAnalytics) {
              handleLinkClick(
                e,
                type,
                dataAnalyticsValue,
                (item.HstMenuItemRepresentation.parameters as MenuParameters).analyticsSatellite
              )
            }
          }
        }}
        ref={usedNavRef}
        data-icon={''}
        data-analytics={dataAnalyticsValue}
        className={`u-linkClean ${
          showDropdown && hasSubmenu && !menuIconDisable ? 'Icon--after ' : ''
        }u-flex u-flexAlignItemsCenter u-paddingVert8 ${disabled && 'u-disabled'}`}
        href={DOMPurify.sanitize(item.HstMenuItemRepresentation.url)}
        style={linkStyle}
        aria-current={activeTab === item.HstMenuItemRepresentation.name ? 'page' : 'false'}
        {...(hasSubmenu
          ? {
              'aria-haspopup': showDropdown && hasSubmenu ? 'true' : 'false',
              'aria-expanded': dropDownVisible,
            }
          : {})}
        data-testid={item.HstMenuItemRepresentation.name}
      >
        {item.HstMenuItemRepresentation.name}
      </a>
      {dropDownVisible && (
        <Dropdown
          depthLevel={depthLevel}
          submenus={item.HstMenuItemRepresentation.menuItems || []}
          dropdown={dropDownVisible}
          textSizeClass={textSizeClass}
          totalSubmenus={totalSubmenus}
          styles={styles}
          countDownOffset={0}
          parentDropdownLength={0}
          parentName={item.HstMenuItemRepresentation.name}
          enableAnalytics={enableAnalytics}
          focusFirstItem={focusFirstItem}
          parentRef={parentRef}
          setDropDownVisible={() => {
            setDropDownVisible(false)
            usedNavRef.current?.focus()
            setFocusFirstItem(false)
            setMouseOver(false)
          }}
        />
      )}
    </li>
  )
}
export default NavMenuItem
